import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type AttendanceAdjustmentType = "add" | "deduct";

export type AttendanceAdjustmentRow = {
  id: string;
  employeeId: string;
  employeeName: string | null;
  adjustmentDate: string;
  adjustmentType: AttendanceAdjustmentType;
  minutes: number;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapType(raw: unknown): AttendanceAdjustmentType {
  return str(raw) === "deduct" ? "deduct" : "add";
}

function mapRow(raw: Record<string, unknown>): AttendanceAdjustmentRow {
  const minutesRaw = raw.minutes;
  const minutes =
    typeof minutesRaw === "number" && Number.isFinite(minutesRaw)
      ? Math.max(0, Math.round(minutesRaw))
      : Math.max(0, Math.round(Number(minutesRaw) || 0));

  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    adjustmentDate: str(raw.adjustment_date).slice(0, 10),
    adjustmentType: mapType(raw.adjustment_type),
    minutes,
    reason: raw.reason != null ? str(raw.reason) : null,
    createdBy: raw.created_by != null ? str(raw.created_by) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

/** Format stored minutes as e.g. "1h 30m" or "45m". */
export function formatAdjustmentMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0m";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function attendanceAdjustmentsApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listAttendanceAdjustments(params: {
  employeeId: string;
  adjustmentDate: string;
}): Promise<AttendanceAdjustmentRow[]> {
  const response = await api.get<{ data?: Record<string, unknown>[] }>("/api/v1/attendance/adjustments", {
    params: {
      employee_id: params.employeeId,
      adjustment_date: params.adjustmentDate,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return list.map((r) => mapRow(r as Record<string, unknown>));
}

export async function createAttendanceAdjustments(payload: {
  employeeId: string;
  adjustmentDate: string;
  lines: Array<{
    adjustmentType: AttendanceAdjustmentType;
    minutes: number;
    reason?: string | null;
  }>;
}): Promise<{ rows: AttendanceAdjustmentRow[]; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown>[] }>(
    `/api/v1/employees/${encodeURIComponent(payload.employeeId)}/attendance-adjustments`,
    {
      adjustment_date: payload.adjustmentDate,
      lines: payload.lines.map((line) => ({
        adjustment_type: line.adjustmentType,
        minutes: line.minutes,
        reason: line.reason ?? null,
      })),
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Attendance adjustment recorded.",
  };
}

export async function deleteAttendanceAdjustment(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/attendance/adjustments/${encodeURIComponent(id)}`,
  );
  return { message: str(response.data?.message) || "Attendance adjustment deleted." };
}
